<template>
  <div>
    <data-access-violation-full-details
      :access-violation="userDataAccessViolation"
      :ticket-details="ticketDetails"
    />
    <template v-if="hideSensitiveData">
      <div class="d-flex flex-column align-center justify-center pa-7 sensitive-data-container">
        <v-icon size="80" icon="$dataNoHexagon"></v-icon>
        <span class="subtitle1">{{ $t("general.accessToSensitiveDataRestricted") }}</span>
      </div>
    </template>

    <component
      v-else
      :is="fullDetailsComponent"
      :key="`${ticketDetails.eventId}-${ticketDetails.eventTrigger}`"
      :ticket="ticketDetails"
      :show-title="!userDataAccessViolation"
      :context="config.context"
    ></component>
  </div>
</template>

<script setup lang="ts" generic="T extends TicketDetails">
import { computed } from "vue";
import { TicketType, ViolationDirection } from "@/constants/tickets.ts";
import DataAccessViolationFullDetails from "./UserDataAccessViolationFullDetails.vue";
import UnifiedDeviceDlpFindingsFullDetails from "@/components/tickets/ticket-preview-details/full-details/UnifiedDeviceDlpFindingsFullDetails.vue";
import {
  type TicketTriggerToFullDetailsComponentMap,
  type TicketTypeToFullDetailsComponentMap,
} from "@/types.ts";
import type { TicketDetails } from "@/_store/tickets/tickets.module.ts";
import {
  defaultTicketFullDetailsConfig,
  type TicketFullDetailsConfig,
} from "./helpers/ticket-full-details.config.ts";
import { markComponentAsPlain } from "@/_helpers/dom";

const props = withDefaults(
  defineProps<{
    ticketDetails: T;
    config?: TicketFullDetailsConfig;
  }>(),
  {
    config: () => ({ ...defaultTicketFullDetailsConfig }),
  }
);

const fullDetailsComponent = computed(() => {
  const { eventType, eventTrigger, sections } = props.ticketDetails;

  // Cloud-share unified DLP renders UnifiedDeviceDlpFindingsFullDetails for non-legacy variant,
  // filesMetaData[0]?.fileName only present in legacy variants
  if (
    eventType === TicketType.SUSPECTED_UNIFIED_SENSITIVE_DATA_VIOLATION_BY_SHARING &&
    !sections?.filesMetaData[0]?.fileName
  ) {
    return markComponentAsPlain(UnifiedDeviceDlpFindingsFullDetails);
  }

  const component = props.config.components[eventType as keyof TicketTypeToFullDetailsComponentMap];
  if (component) {
    return markComponentAsPlain(component);
  }
  const triggerComponent =
    props.config.triggerComponents[eventTrigger as keyof TicketTriggerToFullDetailsComponentMap] ??
    null;

  if (!triggerComponent) {
    return null;
  }

  return markComponentAsPlain(triggerComponent);
});

const hideSensitiveData = computed(
  () =>
    props.config.sensitiveTypes.includes(props.ticketDetails.eventType) &&
    props.ticketDetails.sensitiveDataRestricted
);

const userDataAccessViolation = computed(() => {
  const isDlpTicket = props.config.dlpTriggers.includes(props.ticketDetails.eventTrigger);
  if (!isDlpTicket) {
    return undefined;
  }
  const accessViolation = props.ticketDetails.sections?.userDataAccessViolation;
  if (accessViolation) {
    return accessViolation;
  }
  return {
    violationDirection: ViolationDirection.SHARE,
    users: [props.ticketDetails.sections?.triggeredUser],
  };
});
</script>

<style scoped lang="scss">
.full-details-wrapper {
  border: 1px solid rgb(var(--v-theme-indigo-faint));
}
.icon-full-details-l:before {
  color: rgb(var(--v-theme-inactive)) !important;
}
.sensitive-data-container {
  border: 1px solid rgb(var(--v-theme-indigo-faint));
}

.icon-data-no-hex-l:before {
  color: rgb(var(--v-theme-indigo-light)) !important;
}
</style>
